import {connect, ConnectedProps} from "react-redux";
import {
    Checkbox,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem
} from "@mui/material";
import {CameraAlt, InsertDriveFile, PhotoLibrary} from "@mui/icons-material";
import TextArea from "components/elements/TextArea";
import SelectOptionsView from "pages/Contracts/SelectOptionsView";
import "pages/Styles/UIKit_v2.sass";
import "pages/Styles/EditContractEntities.sass";
import {ReactComponent as Delete} from "../Icons/size24/Delete.svg";
import {ReactComponent as CheckboxIcon} from "pages/Icons/size24/Checkbox.svg";
import {ReactComponent as CheckboxChecked} from "pages/Icons/size24/CheckboxChecked.svg";
import {boolEntityToValue, BOOL_ENTITY_TYPE, isBoolEntityChecked, normalizeEntityList, valueOrEntityDefault} from "../../methods/workWithEntities";
import React, {useEffect, useImperativeHandle, useRef, useState} from "react";
import notifyUser from "../../methods/notifyUser";
import {formatBytes} from "../../methods/formatBytes";
import TextFieldMasked from "../../components/elements/TextFieldMasked";
import {trackGoal} from "../../methods/trackGoal";
import {clientPassportNumber} from "../../methods/SystemEntity";
import { SignaturePopup } from "./SignaturePopup";
import { sanitizeUntrustedHtml } from "../../utils/sanitizeHtml";
import { Refresh } from "@mui/icons-material";
import { getEntityPatterns } from "methods/emptyEntities";
import { displayEntityKeyword } from "../../methods/workWithObjectEntity";
import { getBrowserInfo } from "../../methods/browserInfo";
import { processFile, getBase64, createPdfPreview, getVerificationBase64Raw, processFileViaServer } from "../../methods/photoProcessing";
import { ParagraphEntity, ParagraphValue } from "./EntityForm";
import PdfViewer from "../../components/PdfViewer";
import EntityFieldPhotoViewerPopup from "components/entityFieldPhotoView/EntityFieldPhotoViewerPopup";
import { photoSrcFromValue } from "../../methods/photoFieldHtml";
import {TimePickerField} from './TimePickerField';
import {DatePickerField} from './DatePickerField';
import {extractTimeFromValue, formatDisplayDate} from "../../utils/dateUtils";
import {$moment} from "../../funcs";
import {UI_LAYER_DROPDOWN_OVER_POPUP} from "../../constants/uiLayers";

type Props = {
    myRef: React.RefObject<any>,
    entity: any,
    previousDateForCalendar?: string,
    disabled?: boolean,
    updateEntity: Function,
    setImageStringTag: boolean,
    maxWidthOrHeight: number,
    maxImageWidth?: string | number,
    /** Отдельная обработка для автопроверки селфи (меньше сжатие, без водяного знака) */
    beorgVerificationPhoto?: boolean,
    onBeorgVerificationRawBase64?: (rawPayload: string) => void,
    /**
     * Мягкая проверка «на фото есть лицо» (MediaPipe).
     * Включается только для поля селфи при флаге с бэка; ошибка библиотеки игнорируется.
     */
    checkFacePresence?: boolean,
    /** Макс. число строк autosize TextArea (по умолчанию 12) */
    textAreaMaxRows?: number,
    /** Для типа «Галочка»: подпись в одной строке с чекбоксом */
    checkboxInlineLabel?: string,
    checkboxShowRequired?: boolean,
    /** Галочка + название в строке заголовка поля (не в блоке инпута) */
    boolCheckboxInTitle?: boolean,
    selectDisableFlip?: boolean,
}

// Добавляем функцию для безопасного преобразования значения в строку
function safeValueToString(value: any): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 'Да' : 'Нет';
    }
    if (typeof value === 'number' || typeof value === 'string') {
        return String(value);
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return '';
        }
    }
    return '';
}

function isOnlyFromCameraEnabled(entity: any): boolean {
    return entity.onlyFromCamera === true || entity.onlyFromCamera === "true";
}

function getEntityInputPlaceholder(entity: any, defaultPlaceholder: string): string {
    const custom = entity.inputPlaceholder?.trim();
    return custom || defaultPlaceholder;
}

let EntityInput: any = function EntityInput(props: Props) {
    const {
        myRef,
        disabled,
        entity,
        previousDateForCalendar,
        updateEntity,
        setImageStringTag = true,
        maxWidthOrHeight = 1024,
        maxImageWidth,
        beorgVerificationPhoto = false,
        onBeorgVerificationRawBase64,
        checkFacePresence = false,
        textAreaMaxRows = 12,
        checkboxInlineLabel,
        checkboxShowRequired = false,
        boolCheckboxInTitle = false,
        selectDisableFlip = false,
    } = props
    
    // Вычисляем значение один раз
    const displayValue = safeValueToString(entity.value)

    const focusInput = () => {
        switch (entity.type) {
            case "Текст":
                textInput?.current?.focus()
                // @ts-ignore
                maskInput?.current?.element?.focus()
                break
            case "Фото":
                openUploadSource()
                break
            case "Дата":
                input.current?.focus()
                break
            case "Список":
                input.current?.focus()
                break
            case BOOL_ENTITY_TYPE:
                break
            default:
                input.current?.focus()
        }
    }

    useImperativeHandle(myRef,
        () => focusInput,
        []
    )

    const [isEditing, setIsEditing] = useState(false)
    const [fileName, setFileName] = useState("");
    const [loading, setLoading] = useState(false);
    const [processingStage, setProcessingStage] = useState<string>("");
    const [stageIndex, setStageIndex] = useState(0);
    const [showPdfPopup, setShowPdfPopup] = useState(false);
    const [fieldPhotoView, setFieldPhotoView] = useState<{src: string, alt: string} | null>(null);
    const stageInterval = useRef<NodeJS.Timeout | null>(null);
    const lastValidValueRef = useRef<string>(displayValue);
    const lastNotificationTimeRef = useRef<number | null>(null);

    /** Для поля «Объект» (strictListSelection): последнее значение из списка, к которому откатываемся при неверном вводе */
    const lastValidStrictListValueRef = useRef<string>(displayValue);
    const [listSelectError, setListSelectError] = useState(false);

    useEffect(() => {
        if (!entity.strictListSelection) {
            return;
        }
        const list = normalizeEntityList(entity.list);
        if (list.length === 0) {
            setListSelectError(false);
            return;
        }
        const v = (displayValue ?? '').trim();
        const ok =
            v === '' ||
            list.some((x: string) => (x ?? '').toString().trim() === v);
        if (ok) {
            lastValidStrictListValueRef.current = displayValue;
        }
    }, [displayValue, entity.list, entity.strictListSelection]);

    const processingStages = [
        "Подготавливаем ваше фото...",
        "Добавляем водяной знак...",
        "Оптимизируем изображение...",
        "Почти готово...",
        "Финальная обработка..."
    ];

    const startStageRotation = () => {
        setStageIndex(0);
        setProcessingStage(processingStages[0]);
        
        stageInterval.current = setInterval(() => {
            setStageIndex(prev => {
                const next = (prev + 1) % processingStages.length;
                setProcessingStage(processingStages[next]);
                return next;
            });
        }, 3000 + Math.random() * 4000); // Случайный интервал от 3 до 7 секунд
    };

    const stopStageRotation = () => {
        if (stageInterval.current) {
            clearInterval(stageInterval.current);
            stageInterval.current = null;
        }
        setProcessingStage("");
    };

    useEffect(() => {
        return () => {
            if (stageInterval.current) {
                clearInterval(stageInterval.current);
            }
        };
    }, []);

    // Функция для показа уведомления с дебаунсом
    function showNumberInputNotification() {
        const now = Date.now();
        const lastNotificationTime = lastNotificationTimeRef.current;
        
        // Если это первое уведомление или прошло 20 секунд с последнего показа
        if (lastNotificationTime === null || (now - lastNotificationTime) >= 10000) {
            // Показываем уведомление сразу
            notifyUser(props, "В это поле можно вводить только числа", "Для того чтобы вводить любые символы, отключите настройку «Текстовая форма числа»");
            lastNotificationTimeRef.current = now;
        }
    }

    const cameraUploadRef = useRef<HTMLInputElement>(null);
    const galleryUploadRef = useRef<HTMLInputElement>(null);
    const fileUploadRef = useRef<HTMLInputElement>(null);
    const input = useRef<HTMLInputElement>(null);
    const textInput = useRef<HTMLTextAreaElement>(null);
    const maskInput = useRef(null);

    const [result, setResult] = useState<any>(null)
    const [uploadSourceAnchorEl, setUploadSourceAnchorEl] = useState<HTMLElement | null>(null);
    const [cameraAvailable, setCameraAvailable] = useState<boolean | null>(null);
    /** Мягкое предупреждение «на селфи не видно лица» — висит под превью, не toast. */
    const [facePresenceWarning, setFacePresenceWarning] = useState<string | null>(null);

    const onlyFromCameraFlag = isOnlyFromCameraEnabled(entity);
    const isPdfEntity = entity.type === "PDF";

    const cameraOptionAvailable = !isPdfEntity && cameraAvailable !== false;
    const galleryOptionAvailable = !isPdfEntity && !onlyFromCameraFlag;
    const filesOptionAvailable = isPdfEntity || !onlyFromCameraFlag;

    useEffect(() => {
        if (!navigator.mediaDevices?.enumerateDevices) {
            setCameraAvailable(null);
            return;
        }

        navigator.mediaDevices.enumerateDevices()
            .then((devices) => {
                setCameraAvailable(devices.some((device) => device.kind === "videoinput"));
            })
            .catch(() => {
                // Если браузер не дал перечислить устройства без разрешения,
                // считаем камеру потенциально доступной и оставляем выбор пользователю.
                setCameraAvailable(null);
            });
    }, []);

    useEffect(() => {
        if (result) {
            updateEntity(result)
        } 
    }, [result])

    // Синхронизируем lastValidValueRef с displayValue при изменении entity.value
    useEffect(() => {
        if (entity.needNumberToWords) {
            const regex = /^-?\d*[.,]?\d*$/;
            const normalizedValue = displayValue.replace(/\s/g, '');
            if (displayValue === '' || regex.test(normalizedValue)) {
                lastValidValueRef.current = displayValue;
            }
        } else {
            lastValidValueRef.current = displayValue;
        }
    }, [displayValue, entity.needNumberToWords])

    // Функция для обработки ошибок при загрузке фото
    async function handlePhotoError(error: unknown, files: FileList | null) {
        console.error('Детальная ошибка при обработке фото:', error);
        
        const errorInfo = {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            raw_error: error
        };
        
        if (errorInfo.message !== 'Файл не выбран') {
            const browserInfo = getBrowserInfo();
            const errorParams = {
                photo_error: errorInfo.message,
                error_stack: errorInfo.stack,
                error_type: error?.constructor?.name || typeof error,
                raw_error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
                browser_info: browserInfo,
                memory_usage: window.performance && 'memory' in window.performance ? {
                    jsHeapSizeLimit: formatBytes((performance as any).memory.jsHeapSizeLimit),
                    totalJSHeapSize: formatBytes((performance as any).memory.totalJSHeapSize),
                    usedJSHeapSize: formatBytes((performance as any).memory.usedJSHeapSize)
                } : undefined
            };

            trackGoal("take_photo_error", errorParams);

            // Показываем пользователю сообщение об ошибке из самой ошибки
            notifyUser(props, errorInfo.message);
        } else {
            notifyUser(props, 'Файл не выбран');
        }
    }

    // Серверный фолбэк обработки фото с логированием для наблюдения.
    // Бросает исходную клиентскую ошибку, если сервер тоже не справился —
    // её сообщение информативнее для пользователя.
    async function processViaServerFallback(file: File, clientError: unknown, addWatermark: boolean): Promise<string> {
        const browserInfo = getBrowserInfo();
        const fileInfo = {
            file_type: file.type,
            file_name: file.name,
            file_size: formatBytes(file.size),
        };

        trackGoal("take_photo_fallback_used", {
            reason: clientError instanceof Error ? clientError.message : String(clientError),
            ...fileInfo,
            browser_info: browserInfo,
        });

        try {
            const html = await processFileViaServer(file, maxWidthOrHeight, setImageStringTag, addWatermark);
            trackGoal("take_photo_fallback_success", {
                ...fileInfo,
                browser_info: browserInfo,
            });
            return html;
        } catch (serverError) {
            trackGoal("take_photo_fallback_error", {
                client_error: clientError instanceof Error ? clientError.message : String(clientError),
                server_error: serverError instanceof Error ? serverError.message : String(serverError),
                ...fileInfo,
                browser_info: browserInfo,
            });
            throw clientError;
        }
    }

    async function processPhotoInput(files: FileList | null) {
        setLoading(true);
        startStageRotation();
        try {
            const file = files && files.length ? files[0] : null;
            
            if (!file) {
                throw new Error('Файл не выбран');
            }

            // Страховка для onlyFromCamera: некоторые Android-браузеры (Samsung
            // Internet, отдельные WebView, MIUI Browser) игнорируют атрибут
            // capture на уровне ОС и всё равно дают выбрать файл из галереи.
            // Если флаг включён, разрешаем строго изображение.
            const onlyFromCameraStrict = isOnlyFromCameraEnabled(entity);
            if (onlyFromCameraStrict && entity.type !== "PDF") {
                const fileNameLower = (file.name || "").toLowerCase();
                const isImageByMime = typeof file.type === "string" && file.type.startsWith("image/");
                const isImageByExt = /\.(jpe?g|png|gif|heic|webp|bmp)$/.test(fileNameLower);
                if (!isImageByMime && !isImageByExt) {
                    trackGoal("only_from_camera_violation", {
                        file_type: file.type,
                        file_name: file.name,
                        browser_info: getBrowserInfo(),
                    });
                    throw new Error(
                        'Это поле требует фотографию, сделанную камерой. ' +
                        'Похоже, что ваш браузер открыл галерею или файлы вместо камеры. ' +
                        'Пожалуйста, нажмите кнопку «Сделать фото» ещё раз и сфотографируйте документ. ' +
                        'Если камера всё равно не открывается, попробуйте другой браузер (например, Chrome).'
                    );
                }
            }

            if (entity.type === "PDF") {
                // Для PDF файлов сохраняем оригинал в base64
                setFileName(file.name);
                
                // Проверяем размер файла (максимум 10MB)
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    throw new Error(`Размер PDF файла превышает 10MB. Текущий размер: ${formatBytes(file.size)}`);
                }
                
                // Сохраняем оригинальный PDF в base64
                const pdfReader = new FileReader();
                pdfReader.onload = () => {
                    const pdfBase64 = pdfReader.result as string;
                    setResult(pdfBase64);
                };
                pdfReader.onerror = () => {
                    throw new Error('Не удалось прочитать PDF файл. Возможно, файл поврежден.');
                };
                pdfReader.readAsDataURL(file);
            } else {
                const addWatermark = entity.addWatermark ?? true;

                // Отдельный кадр для Beorg: не блокируем разводку превью/подписи и не роняем основной процесс загрузки
                const queueBeorgVerification = (fileForVerification: File) => {
                    if (!(beorgVerificationPhoto && onBeorgVerificationRawBase64)) {
                        return;
                    }
                    const pushBeorgVerification = async () => {
                        try {
                            const rawVerification = await getVerificationBase64Raw(fileForVerification);
                            onBeorgVerificationRawBase64(rawVerification);
                        } catch (beorgVerificationError) {
                            console.warn("Не удалось подготовить копию для проверки (Beorg)", beorgVerificationError);
                        }
                    };
                    queueMicrotask(() => void pushBeorgVerification());
                };

                // Мягкая проверка лица: не блокирует загрузку; сбой библиотеки = тихий skip.
                // faceDetection + MediaPipe подгружаются только здесь (lazy), не при открытии страницы.
                const queueFacePresenceCheck = (fileForFaceCheck: File) => {
                    if (!checkFacePresence) {
                        return;
                    }
                    const runFacePresenceCheck = async () => {
                        try {
                            const { checkFacePresenceInImageFile } = await import("../../methods/faceDetection");
                            const presence = await checkFacePresenceInImageFile(fileForFaceCheck);
                            if (presence === "no_face") {
                                trackGoal("selfie_face_not_detected", {
                                    browser_info: getBrowserInfo(),
                                });
                                setFacePresenceWarning(
                                    "На фото не видно лица. Сделайте селфи так, чтобы лицо было хорошо видно — так проверка пройдёт быстрее. Фото уже сохранено, при необходимости можно переснять."
                                );
                                return;
                            }
                            setFacePresenceWarning(null);
                        } catch (faceCheckError) {
                            console.warn("Face presence check skipped", faceCheckError);
                            setFacePresenceWarning(null);
                        }
                    };
                    queueMicrotask(() => void runFacePresenceCheck());
                };

                try {
                    // Основной путь: обработка в браузере (бережём трафик).
                    setFacePresenceWarning(null);
                    const processedFile = await processFile(file);
                    setFileName(processedFile.name);
                    const resultHtml = await getBase64(processedFile, maxWidthOrHeight, setImageStringTag, addWatermark);
                    setResult(resultHtml);
                    queueBeorgVerification(processedFile);
                    queueFacePresenceCheck(processedFile);
                } catch (clientError) {
                    // Фолбэк: браузер не справился — отдаём оригинал на серверную обработку.
                    setFacePresenceWarning(null);
                    const resultHtml = await processViaServerFallback(file, clientError, addWatermark);
                    setFileName(file.name);
                    setResult(resultHtml);
                    queueBeorgVerification(file);
                    queueFacePresenceCheck(file);
                }
            }
        } catch (error) {
            await handlePhotoError(error, files);
        } finally {
            setLoading(false);
            stopStageRotation();
        }
    }

    function processSelectionOfEntity(entity: any) {
        let {normalPattern, escapedPattern} = getEntityPatterns(entity.keyword)
        for (let name of [normalPattern, escapedPattern]) {
            document.getElementsByName(name).forEach((element: HTMLElement) => {
                element.style.setProperty("border", "1px solid #DE9B94");
                element.style.setProperty("transition-duration", "200ms");
                element.style.setProperty("transition-property", "background");
                element.style.setProperty(
                    "background",
                    isEditing ? "#DE9B94" : "None",
                )
            })
        }
    }

    useEffect(
        () => {
            processSelectionOfEntity(entity)
        },
        []
    )

    useEffect(
        () => {
            setTimeout(() => {
                processSelectionOfEntity(entity)
            }, 50);
        },
        [isEditing]
    )

    // Функция для обработки даты
    function handleDateChange(dateValue: string) {
        if (entity.type === "Дата") {
            // Триммим значение
            const trimmedValue = dateValue?.trim() || '';
            
            // Если строка пустая, сразу очищаем значение
            if (!trimmedValue) {
                updateEntity('', undefined, '');
                return;
            }

            // dateValue в формате DD.MM.YYYY
            // Парсим текущий raw_value в объект даты
            let currentDateObj = null;
            
            if (entity.raw_value) {
                // Теперь всегда используем ISO формат
                currentDateObj = $moment(entity.raw_value);
            }
            
            if (currentDateObj && currentDateObj.isValid()) {
                // Парсим новую дату
                const newDateObj = $moment(trimmedValue, 'DD.MM.YYYY');
                
                if (newDateObj.isValid()) {
                    // Создаем новый объект с новой датой, но сохраняем время
                    const finalDateObj = newDateObj
                        .hour(currentDateObj.hour())
                        .minute(currentDateObj.minute());
                    
                    // Форматируем raw_value в ISO формате (без UTC конвертации) и value
                    const newRawValue = finalDateObj.format('YYYY-MM-DDTHH:mm:ss');
                    const newValue = formatDisplayDate(newRawValue, entity.includeTime);
                    
                    updateEntity(newValue, undefined, newRawValue);
                }
            } else {
                // Если raw_value нет, просто обновляем
                const newDateObj = $moment(trimmedValue, 'DD.MM.YYYY');
                if (newDateObj.isValid()) {
                    const newRawValue = newDateObj.format('YYYY-MM-DDTHH:mm:ss');
                    const newValue = formatDisplayDate(newRawValue, entity.includeTime);
                    updateEntity(newValue, undefined, newRawValue);
                }
            }
        }
    }

    // Функция для обработки времени
    function handleTimeChange(timeValue: string) {
        if (entity.type === "Дата" && entity.includeTime) {
            // Триммим значение времени
            const trimmedTimeValue = timeValue?.trim() || '';
            
            // timeValue в формате HH:mm
            // Парсим текущий raw_value в объект даты
            let currentDateObj = null;
            
            if (entity.raw_value) {
                // Теперь всегда используем ISO формат
                currentDateObj = $moment(entity.raw_value);
            }
            
            if (currentDateObj && currentDateObj.isValid()) {
                // Разбираем время на часы и минуты
                const [hours, minutes] = trimmedTimeValue.split(':').map(Number);
                
                // Создаем новый объект с тем же днем, но новым временем
                const finalDateObj = currentDateObj
                    .hour(hours)
                    .minute(minutes);
                
                // Форматируем raw_value в ISO формате (без UTC конвертации) и value
                const newRawValue = finalDateObj.format('YYYY-MM-DDTHH:mm:ss');
                const newValue = formatDisplayDate(newRawValue, entity.includeTime);
                
                updateEntity(newValue, undefined, newRawValue);
            }
        }
    }

    function makeInput() {
        switch (entity.type) {
            case "Текст":
                return makeTextInput()
            case "Список":
                return makeSelectInput()
            case "Фото":
                if (entity.value || result) {
                    return makePhotoView()
                } else {
                    return makePhotoInput()
                }
            case "PDF":
                if (entity.value) {
                    return makePdfView()
                } else {
                    return makePdfInput()
                }
            case "Дата":
                return (
                    <div>
                        <DatePickerField
                            input={input}
                            isEditing={isEditing}
                            setIsEditing={setIsEditing}
                            entity={entity}
                            initialCalendarDate={previousDateForCalendar}
                            updateEntity={(dateValue: string) => {
                                // Используем handleDateChange из Entity
                                handleDateChange(dateValue);
                            }}
                            disabled={disabled}
                        />
                        
                        {/* Показываем TimePicker только если включено время */}
                        {entity.includeTime && (
                            <TimePickerField
                                entity={entity}
                                currentTime={extractTimeFromValue(entity.value) || "12:00"}
                                onTimeChange={handleTimeChange}
                            />
                        )}
                    </div>
                )
            case "Формула":
                return makeFormulaView()
            case "Подпись":
                return makeSignatureInput()
            case "Абзац":
                return makeParagraphInput()
            case BOOL_ENTITY_TYPE:
                return makeCheckboxInput()
            default:
                return null
        }
    }

    function makeCheckboxInput() {
        const checked = isBoolEntityChecked(entity.value);
        const labelText = (checkboxInlineLabel ?? entity.keyword ?? "").trim();
        const rowClassName = boolCheckboxInTitle
            ? "entity-bool-field entity-bool-field--in-title"
            : "entity-input-v2 entity-bool-field ui-kit-v2";

        return (
            <div className={rowClassName}>
                <Checkbox
                    className="entity-bool-field__checkbox"
                    icon={<CheckboxIcon className="icon"/>}
                    checkedIcon={<CheckboxChecked className="icon"/>}
                    checked={checked}
                    disabled={disabled}
                    onChange={({target: {checked: nextChecked}}) => {
                        if (disabled) return;
                        updateEntity(boolEntityToValue(nextChecked));
                    }}
                />
                {labelText ? (
                    <span className="entity-bool-field__label">
                        {labelText}
                        {checkboxShowRequired && <span className="entity-bool-field__required">*</span>}
                    </span>
                ) : null}
            </div>
        );
    }

    function makeTextInput() {
        // Функция для округления значения
        const roundValue = (value: string, roundTo?: number): string => {
            if (roundTo === undefined || roundTo === null) {
                return value;
            }
            
            // Пробуем распарсить значение как число
            const normalizedValue = value.replace(/\s/g, '').replace(',', '.');
            const numValue = parseFloat(normalizedValue);
            
            // Если это не число, возвращаем исходное значение
            if (isNaN(numValue)) {
                return value;
            }
            
            // Округляем значение
            if (Math.floor(numValue) === numValue) {
                return String(Math.floor(numValue));
            }
            return String(Number(numValue.toFixed(roundTo)));
        };

        return <div className="entity-input-v2 ui-kit-v2">
        <TextArea
            ref={textInput}
            value={displayValue}
            placeholder={getEntityInputPlaceholder(
                entity,
                entity.needNumberToWords ? "Введите число" : "Введите значение",
            )}
            disabled={disabled}
            minRows={1}
            maxRows={textAreaMaxRows}
            inputMode={entity.needNumberToWords ? "decimal" : undefined}
            onChange={({target: {value}}: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (disabled) return;
                if (entity.needNumberToWords) {
                    const regex = /^-?\d*[.,]?\d*$/;
                    const normalizedValue = value.replace(/\s/g, '');
                    if (value === '' || regex.test(normalizedValue)) {
                        updateEntity(value)
                        lastValidValueRef.current = value;
                    } else {
                        if (value !== lastValidValueRef.current) {
                            showNumberInputNotification();
                        }
                    }
                } else {
                    updateEntity(value)
                }
            }}
            onFocus={() => !disabled && setIsEditing(true)}
            onBlur={() => {
                setIsEditing(false);
                let finalValue = displayValue;
                if (finalValue && finalValue.trim() !== finalValue) {
                    finalValue = finalValue.trim();
                }
                if (entity.type === "Текст" && entity.roundToEnabled && entity.roundTo !== undefined && entity.roundTo !== null) {
                    finalValue = roundValue(finalValue, entity.roundTo);
                }
                if (finalValue !== displayValue) {
                    updateEntity(finalValue);
                }
            }}
        />
        </div>
    }

    function makeFormulaView() {
        return <div className="entity-input-v2 ui-kit-v2">
            <TextArea
                ref={textInput}
                value={entity.value ?? ''}
                disabled={true}
                placeholder="Автоматически вычисляется"
                minRows={1}
                maxRows={12}
                onChange={() => {}}
            />
        </div>
    }

    function makeSelectInput() {
        const list = normalizeEntityList(entity.list);
        const listOptions = list.map((s: string) => ({ id: s, title: s }));
        const strict = !!entity.strictListSelection;
        /** Поле «Объект»: список пуст — нечего выбирать */
        const noObjects = strict && list.length === 0;
        const inputDisabled = !!disabled || noObjects;
        const options =
            strict
                ? listOptions
                : displayValue && !list.includes(displayValue)
                    ? [...listOptions, { id: displayValue, title: displayValue }]
                    : listOptions;
        const selectedOption = displayValue ? { id: displayValue, title: displayValue } : null;

        const commitSelectValue = (raw: string) => {
            const next = strict ? (raw ?? "") : valueOrEntityDefault(entity, raw)
            if (next !== displayValue) {
                updateEntity(next)
            }
        }

        const matchesList = (raw: string) => {
            const t = raw.trim();
            if (t === '') {
                return true;
            }
            return list.some((x: string) => (x ?? '').toString().trim() === t);
        };

        return (
            <div className="entity-input-v2 ui-kit-v2">
                <SelectOptionsView
                    options={options}
                    selectedOption={selectedOption}
                    onSelectOption={(option: any) => {
                        const val = typeof option === 'string' ? option : (option?.id ?? option?.title ?? '');
                        commitSelectValue(val);
                        if (strict) {
                            setListSelectError(false);
                        }
                    }}
                    onInputChange={(value) => {
                        if (!inputDisabled) {
                            commitSelectValue(value);
                        }
                        if (strict) {
                            setListSelectError(false);
                        }
                    }}
                    inputValue={displayValue}
                    onFocus={() => {
                        if (!inputDisabled) {
                            setIsEditing(true);
                            if (strict) {
                                setListSelectError(false);
                            }
                        }
                    }}
                    onBlur={() => {
                        setIsEditing(false);
                        if (noObjects) {
                            return;
                        }
                        if (strict) {
                            const raw = (displayValue ?? '').trim();
                            if (raw === '') {
                                setListSelectError(false);
                                if (displayValue !== '') {
                                    updateEntity('');
                                }
                                lastValidStrictListValueRef.current = '';
                                return;
                            }
                            if (matchesList(displayValue)) {
                                setListSelectError(false);
                                if (raw !== displayValue) {
                                    updateEntity(raw);
                                }
                                lastValidStrictListValueRef.current = displayValue;
                                return;
                            }
                            setListSelectError(true);
                            notifyUser(
                                props,
                                'Выберите объект из списка',
                                'Введите значение из списка или выберите объект в выпадающем списке. Произвольный текст не подходит.',
                            );
                            const revert = lastValidStrictListValueRef.current;
                            updateEntity(revert);
                            return;
                        }
                        const next = valueOrEntityDefault(entity, (displayValue ?? "").trim());
                        if (next !== displayValue) {
                            updateEntity(next);
                        }
                    }}
                    placeholder={
                        noObjects
                            ? 'Нет объектов — нажмите ⚙️, затем «+» у списка объектов'
                            : getEntityInputPlaceholder(
                                entity,
                                strict
                                    ? 'Выберите объект из списка…'
                                    : 'Введите или выберите значение...',
                            )
                    }
                    freeSolo={true}
                    width="full"
                    inputRef={input}
                    disabled={inputDisabled}
                    error={strict && listSelectError && !noObjects}
                    disableFlip={selectDisableFlip}
                    optionLineClamp={4}
                />
            </div>
        );
    }

    function openNativeInput(ref: React.RefObject<HTMLInputElement>) {
        setUploadSourceAnchorEl(null);
        if (ref.current) {
            ref.current.value = "";
        }
        ref.current?.click();
    }

    function openCameraInput() {
        openNativeInput(cameraUploadRef);
    }

    function openGalleryInput() {
        openNativeInput(galleryUploadRef);
    }

    function openFilesInput() {
        openNativeInput(fileUploadRef);
    }

    function openUploadSource(event?: React.MouseEvent<HTMLElement>) {
        if (disabled || loading) return;

        if (isPdfEntity) {
            openFilesInput();
            return;
        }

        if (onlyFromCameraFlag) {
            openCameraInput();
            return;
        }

        // Рисуем одинаковый выбор источника сами, а после выбора кликаем по
        // конкретному скрытому input с узким accept/capture.
        if (cameraOptionAvailable || galleryOptionAvailable || filesOptionAvailable) {
            if (!event?.currentTarget) {
                openGalleryInput();
                return;
            }
            setUploadSourceAnchorEl(event?.currentTarget ?? null);
            return;
        }

        openGalleryInput();
    }

    function makePhotoInput() {
        return <div className="entity-input-v2 ui-kit-v2">
            <button
                className="entity-input-v2__upload-btn"
                disabled={disabled}
                onClick={openUploadSource}
            >
                {loading
                    ? <CircularProgress size={24} sx={{ color: "#0070FF" }} />
                    : onlyFromCameraFlag
                        ? <CameraAlt sx={{ width: 24, height: 24, color: "#000" }} />
                        : <PhotoLibrary sx={{ width: 24, height: 24, color: "#000" }} />
                }
                <span className="button-text">
                    {loading
                        ? processingStage
                        : (onlyFromCameraFlag ? "Сделать фото" : "Загрузить фото")
                    }
                </span>
            </button>
        </div>
    }

    async function handleNativeFileChange(files: FileList | null) {
        setUploadSourceAnchorEl(null);
        try {
            await processPhotoInput(files);
        } catch (error) {
            console.error('Ошибка при обработке фото:', error);
            await handlePhotoError(error, files);
        }
    }

    const photoInput = (
        <>
            <input
                type={"file"}
                accept="image/*"
                capture="environment"
                tabIndex={-1}
                ref={cameraUploadRef}
                style={{display: "none"}}
                onChange={({target: {files}}) => {
                    void handleNativeFileChange(files);
                }}
            />
            <input
                type={"file"}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/heic,.heic,.HEIC,image/webp,video/mp4,video/quicktime"
                tabIndex={-1}
                ref={galleryUploadRef}
                style={{display: "none"}}
                onChange={({target: {files}}) => {
                    void handleNativeFileChange(files);
                }}
            />
            <input
                type={"file"}
                accept={isPdfEntity ? "application/pdf" : "application/pdf,.pdf"}
                tabIndex={-1}
                ref={fileUploadRef}
                style={{display: "none"}}
                onChange={({target: {files}}) => {
                    void handleNativeFileChange(files);
                }}
            />
            <Menu
                anchorEl={uploadSourceAnchorEl}
                open={Boolean(uploadSourceAnchorEl)}
                onClose={() => setUploadSourceAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                sx={{ zIndex: UI_LAYER_DROPDOWN_OVER_POPUP }}
                PaperProps={{ className: "entity-input-v2 entity-input-v2__source-menu" }}
                MenuListProps={{ className: "entity-input-v2__source-popup ui-kit-v2" }}
            >
                {cameraOptionAvailable && (
                    <MenuItem
                        className="entity-input-v2__source-option"
                        onClick={openCameraInput}
                    >
                        <ListItemIcon>
                            <CameraAlt />
                        </ListItemIcon>
                        <ListItemText primary="Камера" secondary="Сделать новое фото" />
                    </MenuItem>
                )}
                {galleryOptionAvailable && (
                    <MenuItem
                        className="entity-input-v2__source-option"
                        onClick={openGalleryInput}
                    >
                        <ListItemIcon>
                            <PhotoLibrary />
                        </ListItemIcon>
                        <ListItemText primary="Галерея" secondary="Выбрать фото или видео" />
                    </MenuItem>
                )}
                {filesOptionAvailable && (
                    <MenuItem
                        className="entity-input-v2__source-option"
                        onClick={openFilesInput}
                    >
                        <ListItemIcon>
                            <InsertDriveFile />
                        </ListItemIcon>
                        <ListItemText
                            primary="Файлы"
                            secondary={isPdfEntity ? "Выбрать PDF документ" : "Выбрать PDF файл"}
                        />
                    </MenuItem>
                )}
            </Menu>
        </>
    );

    function makePhotoView() {
        // То, что сохранится в системных полях (родитель), может прийти с задержкой — показываем и локальный result
        const photoDisplayed = entity.value || result;
        const isHtmlContent =
            photoDisplayed && typeof photoDisplayed === "string" && photoDisplayed.includes("<img");
        const photoSrc = photoSrcFromValue(photoDisplayed)
        const photoAlt = entity.keyword || "Фото"
        const openPhoto = () => {
            if (photoSrc) {
                setFieldPhotoView({src: photoSrc, alt: photoAlt})
            }
        }

        return <div className="entity-input-v2 ui-kit-v2">
            {photoDisplayed && (
                isHtmlContent ? (
                    <div
                        className="entity-input-v2__photo-wrap"
                        role={photoSrc ? "button" : undefined}
                        tabIndex={photoSrc ? 0 : undefined}
                        aria-label={photoSrc ? "Просмотр фото" : undefined}
                        style={photoSrc ? {cursor: "pointer"} : undefined}
                        onClick={openPhoto}
                        onKeyDown={(e) => {
                            if (photoSrc && (e.key === "Enter" || e.key === " ")) {
                                e.preventDefault()
                                openPhoto()
                            }
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeUntrustedHtml(photoDisplayed) }}
                    />
                ) : (
                    <div
                        className="entity-input-v2__photo-wrap"
                        role={photoSrc ? "button" : undefined}
                        tabIndex={photoSrc ? 0 : undefined}
                        aria-label={photoSrc ? "Просмотр фото" : undefined}
                        style={photoSrc ? {cursor: "pointer"} : undefined}
                        onClick={openPhoto}
                        onKeyDown={(e) => {
                            if (photoSrc && (e.key === "Enter" || e.key === " ")) {
                                e.preventDefault()
                                openPhoto()
                            }
                        }}
                    >
                        <div style={{
                            maxWidth: "80%",
                            marginLeft: "auto",
                            marginRight: "auto",
                            display: "block",
                            marginBottom: "15px",
                            pageBreakInside: "avoid"
                        }}>
                            <img
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "450px",
                                    width: "auto",
                                    height: "auto",
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    display: "block",
                                    pageBreakInside: "avoid",
                                    borderRadius: "12px"
                                }}
                                src={photoDisplayed}
                                alt={photoAlt}
                            />
                        </div>
                    </div>
                )
            )}

            <div className="entity-input-v2__toolbar">
                <div className="entity-input-v2__toolbar-content">
                    {loading && <CircularProgress size={24} sx={{ color: "#0070FF" }} />}
                    {!loading && (
                        <>
                            <button
                                className="entity-input-v2__toolbar-btn"
                                type="button"
                                tabIndex={-1}
                                onClick={() => {
                                    setFileName("");
                                    setResult(null);
                                    setFacePresenceWarning(null);
                                    updateEntity("");
                                    if (beorgVerificationPhoto && onBeorgVerificationRawBase64) {
                                        onBeorgVerificationRawBase64("");
                                    }
                                }}
                            >
                                <Delete />
                            </button>
                            <button
                                className="entity-input-v2__toolbar-btn"
                                type="button"
                                tabIndex={-1}
                                onClick={openUploadSource}
                            >
                                <Refresh />
                            </button>
                            <div className="entity-input-v2__toolbar-label">
                                {fileName}
                            </div>
                        </>
                    )}
                </div>
                <div className="Body_16_v2_secondary" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                    {formatBytes((photoDisplayed?.toString() ?? "").length ?? 0, 0)}
                </div>
            </div>
            {facePresenceWarning && (
                <div className="entity-input-v2__face-warning" role="status">
                    {facePresenceWarning}
                </div>
            )}
        </div>
    }

    function makePdfInput() {
        return <div className="entity-input-v2 ui-kit-v2">
            <button
                className="entity-input-v2__upload-btn"
                disabled={disabled}
                onClick={openUploadSource}
            >
                {loading
                    ? <CircularProgress size={24} sx={{ color: "#0070FF" }} />
                    : <InsertDriveFile sx={{ width: 24, height: 24, color: "#000" }} />
                }
                <span className="button-text">
                    {loading ? processingStage : "Загрузить PDF документ"}
                </span>
            </button>
        </div>
    }

    function makePdfView() {
        return <div className="entity-input-v2 ui-kit-v2">
            {entity.value && (
                <div
                    className="entity-input-v2__pdf-card"
                    onClick={() => setShowPdfPopup(true)}
                >
                    <div style={{ fontSize: "48px", marginBottom: "10px" }}>📄</div>
                    <div className="Body_16_v2_secondary">{fileName || "PDF документ"}</div>
                    <div className="description-xs" style={{ marginTop: "5px" }}>Нажмите для просмотра</div>
                </div>
            )}

            <div className="entity-input-v2__toolbar">
                <div className="entity-input-v2__toolbar-content">
                    {loading && <CircularProgress size={24} sx={{ color: "#0070FF" }} />}
                    {!loading && (
                        <>
                            <button
                                className="entity-input-v2__toolbar-btn"
                                type="button"
                                tabIndex={-1}
                                onClick={() => {
                                    setFileName("");
                                    updateEntity("");
                                    setResult(null);
                                }}
                            >
                                <Delete />
                            </button>
                            <button
                                className="entity-input-v2__toolbar-btn"
                                type="button"
                                tabIndex={-1}
                                onClick={openUploadSource}
                            >
                                <Refresh />
                            </button>
                            <div className="entity-input-v2__toolbar-label">
                                {fileName}
                            </div>
                        </>
                    )}
                </div>
                <div className="Body_16_v2_secondary" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                    {formatBytes((entity.value?.toString() ?? "").length ?? 0, 0)}
                </div>
            </div>
        </div>
    }

    function makeSignatureInput() {
        const [showSignaturePopup, setShowSignaturePopup] = useState(false);

        const signature = entity.value
        return (
            <div className="entity-input-v2 ui-kit-v2" style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                {signature && (
                    <div style={{ maxWidth: "300px", width: "100%", margin: "10px 0" }}>
                        <img
                            style={{ pageBreakInside: "avoid", borderRadius: "12px", maxWidth: "100%", height: "auto" }}
                            src={signature}
                            alt="Подпись"
                        />
                    </div>
                )}
                <button
                    className="action-button primary"
                    style={{ maxWidth: "100%" }}
                    onClick={() => setShowSignaturePopup(true)}
                    disabled={disabled}
                >
                    <span className="button-text">
                        {entity.value ? "Изменить подпись" : "Расписаться"}
                    </span>
                </button>
                {showSignaturePopup && (
                    <SignaturePopup
                        close={() => setShowSignaturePopup(false)}
                        onSave={(signature: string) => {
                            updateEntity(signature);
                            setShowSignaturePopup(false);
                        }}
                    />
                )}
            </div>
        );
    }

    function makeParagraphInput() {
        const selectedValue = entity.values?.find((v: ParagraphValue) => v.value === entity.value);
        const innerRef = useRef<any>(null);
        const listValues = entity.values?.map((v: ParagraphValue) => v.value) || [];
        const listOptions = listValues.map((s: string) => ({ id: s, title: s }));
        const paragraphDisplayValue = entity.value || '';
        const options = paragraphDisplayValue && !listValues.includes(paragraphDisplayValue)
            ? [...listOptions, { id: paragraphDisplayValue, title: paragraphDisplayValue }]
            : listOptions;
        const selectedOption = paragraphDisplayValue ? { id: paragraphDisplayValue, title: paragraphDisplayValue } : null;

        return <div className="entity-input-v2 ui-kit-v2">
            <SelectOptionsView
                options={options}
                selectedOption={selectedOption}
                onSelectOption={(option: any) => {
                    const val = typeof option === 'string' ? option : (option?.id ?? option?.title ?? '');
                    if (!disabled) updateEntity(val);
                }}
                onInputChange={(value) => {
                    if (!disabled) updateEntity(value);
                }}
                inputValue={paragraphDisplayValue}
                onFocus={() => !disabled && setIsEditing(true)}
                onBlur={() => {
                    setIsEditing(false);
                    if (entity.value && entity.value.trim() !== entity.value) {
                        updateEntity(entity.value.trim());
                    }
                }}
                placeholder={getEntityInputPlaceholder(entity, "Выберите абзац...")}
                freeSolo={true}
                width="full"
                inputRef={input}
                disabled={disabled}
                disableFlip={selectDisableFlip}
            />
            
            {selectedValue && (
                <div style={{marginTop: "15px"}}>
                    {selectedValue.entities.map((innerEntity: ParagraphEntity, index: number) => (
                        <div key={index} style={{marginBottom: "15px"}}>
                            <div className="Subheader_14" style={{marginBottom: "5px"}}>
                                {displayEntityKeyword(innerEntity.keyword)}
                            </div>
                            <div id={`input.${entity.keyword}.${innerEntity.keyword}`}>
                                <EntityInput
                                    myRef={innerRef}
                                    entity={{
                                        ...innerEntity,
                                        keyword: `${entity.keyword}.${innerEntity.keyword}`
                                    }}
                                    updateEntity={(value: string) => {
                                        const values = [...(entity.values || [])];
                                        const paragraphIndex = values.findIndex((v: ParagraphValue) => v.value === entity.value);
                                        if (paragraphIndex !== -1) {
                                            values[paragraphIndex].entities[index].value = value;
                                            updateEntity(entity.value);
                                        }
                                    }}
                                    setImageStringTag={true}
                                    maxWidthOrHeight={1024}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    }

    return (
        <>
            {photoInput}
            {makeInput()}
            {showPdfPopup && entity.value && (
                <PdfViewer
                    pdfUrl={entity.value}
                    fileName={fileName}
                    onClose={() => setShowPdfPopup(false)}
                />
            )}
            <EntityFieldPhotoViewerPopup
                open={Boolean(fieldPhotoView?.src)}
                imageSrc={fieldPhotoView?.src ?? null}
                imageAlt={fieldPhotoView?.alt ?? "Фото"}
                onClose={() => setFieldPhotoView(null)}
            />
        </>
    )
}

EntityInput = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(EntityInput)

export {EntityInput}
